/**
 * Which keystrokes the grid must not swallow.
 *
 * AG Grid consumes **every printable character typed at a focused cell**, and
 * it does so whether or not anything is editable. The path is
 * `processCharacter` → `startRowOrCellEdit` → `startEditing`, and that last one
 * ends:
 *
 * ```js
 * if (!this.isCellEditable() || this.editing || !editService) {
 *   return true;   // ← "true" after doing nothing at all
 * }
 * ```
 *
 * `processCharacter` reads that `true` as "editing started" and calls
 * `preventDefault()`. Community edition has no `editService` at all, so the
 * branch is taken on every cell in this grid — none of which is editable.
 *
 * The cost is not theoretical. The host app opens its symbol search on a letter
 * typed with focus nowhere in particular, and its listener is on `document`
 * while AG Grid's is on the row container — so the grid ran first, consumed the
 * key, and the app correctly stood down on an event already marked handled.
 * The symptom was that typing a ticker stopped working the moment you clicked
 * the screener, and stayed broken until you clicked back out.
 *
 * `suppressKeyboardEvent` is AG Grid's own escape hatch: returning `true` skips
 * its cell handling for that event entirely — `onKeyDown`, `doGridOperations`
 * and `processCharacter` — and nothing prevents the default. So the keystroke
 * carries on to whoever actually wants it.
 *
 * Pure and in `lib/` for `row-cursor.ts`'s reason: the whole content is edge
 * cases, and none of them need a rendered grid to state.
 */

/** The part of a `KeyboardEvent` this reads. */
export interface TypingKeyEvent {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

/**
 * A plain printable character, which a non-editable grid has no use for.
 *
 * The rules, in the order they eliminate:
 *
 * - **One character.** `key.length === 1` excludes the whole named-key space in
 *   one rule — `ArrowDown`, `Enter`, `Escape`, `F3`, `Tab`, `Delete` are all
 *   longer, so no key name has to be listed here to be kept out. Those are keys
 *   the grid genuinely means something by, and it keeps every one of them.
 * - **Space is not ours.** It is one character, but AG Grid toggles row
 *   selection with it, and on a rollup tab — where `ScreenerGrid` installs no
 *   cursor of its own — that is the only way to select from the keyboard.
 * - **No Ctrl / Cmd / Alt.** `Ctrl+C` and `Ctrl+A` are grid operations (copy,
 *   select all) and belong to it. A modifier held means a command, not a
 *   character.
 *
 * Deliberately wider than the host's `a-z` type-to-search rule. The question
 * here is not "does the app want this key" — the grid cannot know that — but
 * "can this grid do anything at all with it", and for a digit or a punctuation
 * mark the answer is equally no. Suppressing only the letters the app happens
 * to bind today would make this a second copy of a rule that lives elsewhere.
 */
export function isPlainTypingKey(event: TypingKeyEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.key === " ") return false;
  return event.key.length === 1;
}
